import React, { useCallback, useEffect, useState } from 'react';
import { getUserId, isMasterAccessActive } from '../../utils/auth';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { deveLembrarHoje, limparLembrete, marcarExibicao } from '../../utils/pushPrompt';
import NotificationPermissionDialog from './NotificationPermissionDialog';

const DELAY_MS = 1200;

const outroModalAberto = () =>
  typeof document !== 'undefined' && Boolean(document.querySelector('[role="dialog"]'));

/**
 * Pedido de permissão de notificações da área do cliente.
 *
 * O cliente é lembrado **uma vez por dia** (`utils/pushPrompt`) até ativar o
 * Web Push neste dispositivo. O navegador exige um gesto do usuário para pedir
 * a permissão, então o pedido é um diálogo com botão — nunca automático.
 *
 * Se houver outro modal na tela (guia de boas-vindas/tour, por exemplo), o
 * pedido é reagendado em vez de desistir: no primeiro acesso o onboarding ocupa
 * a tela e o lembrete, sem isso, nunca apareceria.
 */
const NotificationPermissionPrompt = () => {
  const push = usePushNotifications();
  const userId = getUserId();
  const [solicitacaoPendente, setSolicitacaoPendente] = useState(false);
  const [aberto, setAberto] = useState(false);
  const [tentando, setTentando] = useState(false);

  // Guardas de suporte/servidor/rede: sem VAPID configurado nenhuma permissão é
  // pedida — o navegador ficaria marcado para um envio que não existe. No acesso
  // mestre (admin vendo a área do cliente) também não pedimos: a requisição usa
  // o token mestre e a inscrição acabaria na conta do cliente.
  const servidorPronto =
    push.suportado &&
    push.configurado === true &&
    !push.isLoading &&
    !push.error &&
    !push.ativo &&
    !isMasterAccessActive();

  // `atual || ...` mantém o diálogo aberto: `marcarExibicao` grava "hoje" e, se
  // a condição fosse reavaliada, `deveLembrarHoje` passaria a devolver `false`.
  useEffect(() => {
    if (!servidorPronto) {
      setSolicitacaoPendente(false);
      return;
    }

    setSolicitacaoPendente(
      (atual) => atual || deveLembrarHoje({ userId, permissao: push.permissao })
    );
  }, [servidorPronto, push.permissao, userId]);

  useEffect(() => {
    if (!solicitacaoPendente) {
      setAberto(false);
      return undefined;
    }

    let timer;

    const tentarAbrir = () => {
      if (outroModalAberto()) {
        timer = setTimeout(tentarAbrir, DELAY_MS);
        return;
      }

      // Marca antes de abrir: navegar entre páginas no mesmo dia não repete.
      marcarExibicao(userId);
      setAberto(true);
    };

    timer = setTimeout(tentarAbrir, DELAY_MS);

    return () => clearTimeout(timer);
  }, [solicitacaoPendente, userId]);

  const fechar = useCallback(() => setAberto(false), []);

  const ativar = useCallback(async () => {
    setTentando(true);
    const resultado = await push.ativar();
    setTentando(false);

    if (resultado?.ok) {
      limparLembrete(userId);
      setSolicitacaoPendente(false);
      setAberto(false);
    }
  }, [push, userId]);

  return (
    <NotificationPermissionDialog
      open={aberto}
      bloqueado={push.permissao === 'denied'}
      requerInstalacao={push.precisaInstalar}
      somenteAtivarDispositivo={push.permissao === 'granted'}
      tentando={tentando || push.isPending}
      onFechar={fechar}
      onAtivar={ativar}
    />
  );
};

export default NotificationPermissionPrompt;
